import { Variable } from 'features/entry-form-v2/types';
import { isValidDate } from 'features/entry-form-v2/utils/isValidDate';
import { DateTime } from 'luxon';
import { decodeURIComponentSafe } from 'helpers/generic';
import {
	formatMicroseconds,
	withTimeUnits
} from 'features/entry-form-v2/inputs/time-duration/formatMicroSeconds';

export const parseEntryHistoryValue = ({
	value,
	variable
}: {
	value: unknown;
	variable: Variable;
}): ParsedResult => {
	if (value === null || value === undefined || value === '') {
		return {
			result: 'success',
			variableLabel: variable.variableLabel,
			variableType: variable.variableType,
			value: null
		};
	}

	if (variable.variableType === 'date') {
		if (typeof value === 'string' && isValidDate(value)) {
			return {
				result: 'success',
				variableLabel: variable.variableLabel,
				variableType: 'date',
				value: DateTime.fromISO(value).toFormat('yyyy-MM-dd')
			};
		}
	}

	if (variable.variableType === 'datetime') {
		if (typeof value === 'string' && isValidDate(value)) {
			return {
				result: 'success',
				variableLabel: variable.variableLabel,
				variableType: 'datetime',
				value: DateTime.fromISO(value).toFormat("yyyy-MM-dd'T'HH:mm:ss")
			};
		}
	}

	if (variable.variableType === 'categoryMultiple') {
		if (typeof value === 'string') {
			return {
				result: 'success',
				variableLabel: variable.variableLabel,
				variableType: 'categoryMultiple',
				value: JSON.parse(decodeURIComponentSafe(value))
			};
		}

		if (value instanceof Array) {
			return {
				result: 'success',
				variableLabel: variable.variableLabel,
				variableType: 'categoryMultiple',
				value: value.map(item => decodeURIComponentSafe(item))
			};
		}
	}

	if (variable.variableType === 'category') {
		if (typeof value === 'number') {
			return {
				result: 'success',
				variableLabel: variable.variableLabel,
				variableType: 'category',
				value: value.toString()
			};
		}

		if (typeof value !== 'string') {
			console.error(`Expected category value to be string or number, was ${typeof value}`);
			return { result: 'error', variableLabel: variable.variableLabel, originalValue: value };
		}

		return {
			result: 'success',
			variableLabel: variable.variableLabel,
			variableType: 'category',
			value: decodeURIComponentSafe(value)
		};
	}

	if (variable.variableType === 'file') {
		return {
			result: 'success',
			variableLabel: variable.variableLabel,
			variableType: 'file',
			value: { fileId: value as string, variableName: variable.variableName }
		};
	}

	if (
		variable.variableType === 'float' ||
		variable.variableType === 'integer' ||
		variable.variableType === 'string'
	) {
		return {
			result: 'success',
			variableLabel: variable.variableLabel,
			variableType: variable.variableType,
			value: value.toString()
		};
	}

	if (variable.variableType === 'timeDuration') {
		if (typeof value === 'number') {
			const formattedValue = formatMicroseconds({
				microseconds: value,
				maxTimeUnit: variable.durationFormat.maxTimeUnit,
				minTimeUnit: variable.durationFormat.minTimeUnit,
				ignoreMinTimeUnit: true
			});

			return {
				result: 'success',
				variableLabel: variable.variableLabel,
				variableType: 'timeDuration',
				value: withTimeUnits({
					colonSeparatedValues: formattedValue,
					maxTimeUnit: variable.durationFormat.maxTimeUnit
				})
			};
		}
	}

	return { result: 'error', variableLabel: variable.variableLabel, originalValue: value };
};

export type ParsedResult = ParsedValue | ParsedValueError;
type ParsedValueError = { result: 'error'; variableLabel: string; originalValue: unknown };
type ParsedValue = {
	result: 'success';
	variableLabel: string;
} & (
	| {
			variableType: 'categoryMultiple';
			value: string[];
	  }
	| {
			variableType: 'file';
			value: { fileId: string; variableName: string };
	  }
	| {
			variableType:
				| 'category'
				| 'date'
				| 'datetime'
				| 'float'
				| 'integer'
				| 'string'
				| 'timeDuration'
				| 'userDefinedUnique';
			value: string;
	  }
	| {
			variableType: Variable['variableType'];
			value: null;
	  }
);
